import { useEffect, useRef, useState, useCallback } from "react";
import DailyIframe from "@daily-co/daily-js";
import { Mic, MicOff, Video as VideoIcon, VideoOff, User } from "lucide-react";

// Single participant tile: attaches Daily media tracks to <video>/<audio> on OUR page.
// Rendering audio on our own origin means the user's Join tap unlocks autoplay — no
// stuck "one-way audio" like the cross-origin prebuilt iframe.
function Tile({ p }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const vt = p?.tracks?.video?.state === "playable" ? p.tracks.video.persistentTrack : null;
    const el = videoRef.current;
    if (el) {
      if (vt) {
        const ms = new MediaStream([vt]);
        if (el.srcObject !== ms) el.srcObject = ms;
      } else {
        el.srcObject = null;
      }
    }
  }, [p?.tracks?.video?.state, p?.tracks?.video?.persistentTrack]);

  useEffect(() => {
    if (p?.local) return; // never play our own mic back
    const at = p?.tracks?.audio?.state === "playable" ? p.tracks.audio.persistentTrack : null;
    const el = audioRef.current;
    if (el && at) {
      const ms = new MediaStream([at]);
      if (el.srcObject !== ms) el.srcObject = ms;
      el.play().catch(() => {});
    }
  }, [p?.local, p?.tracks?.audio?.state, p?.tracks?.audio?.persistentTrack]);

  const hasVideo = p?.tracks?.video?.state === "playable";
  const micOff = p?.tracks?.audio?.state !== "playable";

  return (
    <div className="relative rounded-2xl overflow-hidden bg-black/50 border border-white/10 aspect-video" data-testid={`tile-${p.local ? "local" : p.session_id}`}>
      {hasVideo ? (
        <video ref={videoRef} autoPlay playsInline muted={!!p.local} className="w-full h-full object-cover" style={{ transform: p.local ? "scaleX(-1)" : "none" }} />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-white/5">
          <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center"><User className="w-8 h-8 text-gold" /></div>
        </div>
      )}
      {!p.local && <audio ref={audioRef} autoPlay />}
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white text-xs font-semibold">
        {micOff ? <MicOff className="w-3.5 h-3.5 text-destructive" /> : <Mic className="w-3.5 h-3.5 text-forest" />}
        <span className="truncate max-w-[10rem]">{p.user_name || (p.local ? "You" : "Guest")}{p.local ? " (you)" : ""}</span>
      </div>
    </div>
  );
}

// Module-level singleton + deferred destroy so React 18 StrictMode's mount→unmount→mount
// doesn't destroy the call object mid-bundle-load (the "callMachine of undefined" crash).
let sharedCall = null;
let destroyTimer = null;

// Call-object embed. Same props as before: reports presence via onParticipantsChange,
// hands the call object back via onCall (used for reaction recording).
export default function VideoCall({ roomUrl, token, onParticipantsChange, onCall }) {
  const callRef = useRef(null);
  const [participants, setParticipants] = useState([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  const cbRef = useRef({ onParticipantsChange, onCall });
  cbRef.current = { onParticipantsChange, onCall };

  const sync = useCallback(() => {
    const c = callRef.current;
    if (!c) return;
    let parts;
    try { parts = c.participants(); } catch (e) { return; }
    const list = Object.values(parts);
    setParticipants(list);
    const local = parts.local;
    if (local) {
      setMicOn(local.tracks?.audio?.state !== "off" && local.tracks?.audio?.state !== "blocked");
      setCamOn(local.tracks?.video?.state !== "off" && local.tracks?.video?.state !== "blocked");
    }
    if (cbRef.current.onParticipantsChange) {
      cbRef.current.onParticipantsChange(list.map((p) => ({ id: p.session_id, name: p.user_name || (p.local ? "You" : "Guest"), local: !!p.local })));
    }
  }, []);

  useEffect(() => {
    if (!roomUrl || !token) return;
    if (destroyTimer) { clearTimeout(destroyTimer); destroyTimer = null; }

    let call = sharedCall || DailyIframe.getCallInstance();
    if (!call) {
      call = DailyIframe.createCallObject({ subscribeToTracksAutomatically: true });
      sharedCall = call;
    }
    callRef.current = call;
    if (cbRef.current.onCall) cbRef.current.onCall(call);

    const events = ["joined-meeting", "participant-joined", "participant-updated", "participant-left", "track-started", "track-stopped"];
    events.forEach((ev) => call.on(ev, sync));

    const state = call.meetingState();
    if (state === "new" || state === "left-meeting") {
      call.join({ url: roomUrl, token }).then(sync).catch((e) => console.error("[VideoCall] join err", e));
    } else {
      sync();
    }

    return () => {
      events.forEach((ev) => call.off(ev, sync));
      // Defer teardown; a StrictMode remount within 400ms cancels it.
      destroyTimer = setTimeout(() => {
        call.leave().catch(() => {});
        call.destroy().catch(() => {});
        if (sharedCall === call) sharedCall = null;
        destroyTimer = null;
      }, 400);
    };
  }, [roomUrl, token, sync]);

  const toggleMic = () => { const c = callRef.current; if (c) { c.setLocalAudio(!micOn); setMicOn(!micOn); } };
  const toggleCam = () => { const c = callRef.current; if (c) { c.setLocalVideo(!camOn); setCamOn(!camOn); } };

  const ordered = [...participants].sort((a, b) => (a.local === b.local ? 0 : a.local ? 1 : -1));
  const count = ordered.length;
  const gridCols = count <= 1 ? "grid-cols-1" : count <= 4 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className="relative h-full w-full flex flex-col gap-3" data-testid="video-call-frame">
      <div className={`flex-1 grid ${gridCols} gap-3 auto-rows-fr content-center overflow-auto`}>
        {ordered.map((p) => <Tile key={p.session_id} p={p} />)}
        {count === 0 && (
          <div className="flex items-center justify-center text-foreground/50 text-sm">Connecting to the call…</div>
        )}
      </div>
      <div className="flex items-center justify-center gap-3 pt-1">
        <button type="button" onClick={toggleMic} data-testid="toggle-mic-btn" className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${micOn ? "bg-white/10 hover:bg-white/20 text-white" : "bg-destructive text-white"}`}>
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button type="button" onClick={toggleCam} data-testid="toggle-cam-btn" className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${camOn ? "bg-white/10 hover:bg-white/20 text-white" : "bg-destructive text-white"}`}>
          {camOn ? <VideoIcon className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
